/**
 * Сервис для регистрации устройства с ожиданием одобрения администратором
 */
import { apiClient } from './apiClient'
import { useDeviceStore } from '../store/deviceStore'
import { initConfig } from '../offline/db'
import { LogsRepo } from '../offline/logsRepo'
import { EventsRepo } from '../offline/eventsRepo'
import { EmployeesRepo } from '../offline/employeesRepo'
import { detectDevice } from '../utils/deviceDetection'

export interface RegistrationStatus {
  is_approved: boolean
  device_id?: string
  device_secret?: string
  site_id?: string
  device_name?: string
  message?: string
}

export class DeviceRegistrationService {
  /**
   * Генерирует уникальный device_id если его нет
   */
  static generateDeviceId(): string {
    // Генерируем UUID v4
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  /**
   * Получает device fingerprint для идентификации устройства
   */
  static getDeviceFingerprint(): string {
    // Используем комбинацию user agent и screen resolution
    const screenInfo = `${window.screen.width}x${window.screen.height}`
    const userAgent = navigator.userAgent
    const platform = navigator.platform || ''
    
    // Создаем простой fingerprint
    const fingerprint = `${userAgent}|${screenInfo}|${platform}`
    
    // Хешируем для конфиденциальности (простой hash)
    let hash = 0
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32bit integer
    }
    
    return Math.abs(hash).toString(16)
  }

  /**
   * Отправляет запрос на регистрацию устройства
   */
  static async requestRegistration(deviceId: string): Promise<void> {
    try {
      console.log('[DeviceRegistrationService] 🚀 Starting registration request...', {
        deviceId,
        isOnline: navigator.onLine,
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL
      })
      
      const deviceFingerprint = this.getDeviceFingerprint()
      const userAgent = navigator.userAgent
      const deviceInfo = detectDevice()

      console.log('[DeviceRegistrationService] 📤 Sending POST /tablet/register', {
        device_id: deviceId,
        device_fingerprint: deviceFingerprint,
        device_info: deviceInfo,
        user_agent: userAgent.substring(0, 50) + '...'
      })

      await apiClient.post('/tablet/register', {
        device_id: deviceId,
        device_fingerprint: deviceFingerprint,
        user_agent: userAgent,
        device_type: deviceInfo.type,
        device_model: deviceInfo.model,
        os_name: deviceInfo.os,
        os_version: deviceInfo.osVersion,
        screen_resolution: deviceInfo.screenResolution,
        browser_name: deviceInfo.browser,
        browser_version: deviceInfo.browserVersion
      })

      console.log('[DeviceRegistrationService] ✅ Registration request sent successfully')

      await LogsRepo.save('info', 'Registration request sent', {
        device_id: deviceId,
      })
    } catch (error) {
      console.error('[DeviceRegistrationService] ❌ Registration request failed:', {
        error,
        errorType: typeof error,
        errorName: error instanceof Error ? error.name : 'unknown',
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        isOnline: navigator.onLine
      })
      
      const errorMsg = error instanceof Error ? error.message : JSON.stringify(error)
      await LogsRepo.save('error', `Registration request failed: ${errorMsg}`, {
        device_id: deviceId,
        error: errorMsg,
      })
      throw error
    }
  }

  /**
   * Проверяет статус регистрации (polling)
   */
  static async checkRegistrationStatus(deviceId: string): Promise<RegistrationStatus> {
    try {
      console.log('[DeviceRegistrationService] 🔍 Checking registration status for device:', deviceId)
      
      const response = await apiClient.get<RegistrationStatus>(
        `/tablet/register/status/${deviceId}`
      )
      
      console.log('[DeviceRegistrationService] 📥 Registration status received:', response)
      return response
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to check registration status'
      await LogsRepo.save('error', `Registration status check failed: ${errorMsg}`, {
        device_id: deviceId,
        error: errorMsg,
      })
      throw error
    }
  }

  /**
   * Запускает polling проверки статуса регистрации
   */
  static startRegistrationPolling(
    deviceId: string,
    onApproved: (status: RegistrationStatus) => void,
    intervalMs: number = 5000
  ): () => void {
    let intervalId: number | null = null
    let isPolling = true

    const poll = async () => {
      if (!isPolling) return

      try {
        const status = await this.checkRegistrationStatus(deviceId)
        
        if (status.is_approved && status.device_id && status.device_secret && status.site_id) {
          // Устройство одобрено, останавливаем polling
          if (intervalId !== null) {
            clearInterval(intervalId)
            intervalId = null
          }
          
          // Вызываем callback
          onApproved(status)
        }
      } catch (error) {
        console.error('Registration polling error:', error)
        // Продолжаем polling даже при ошибке
      }
    }

    // Начинаем polling сразу
    poll()

    // Затем периодически
    intervalId = window.setInterval(poll, intervalMs)

    // Возвращаем функцию для остановки
    return () => {
      isPolling = false
      if (intervalId !== null) {
        clearInterval(intervalId)
      }
    }
  }

  /**
   * Сохраняет credentials устройства и выполняет миграции
   */
  static async completeRegistration(
    deviceId: string,
    deviceSecret: string,
    siteId: string
  ): Promise<void> {
    try {
      const deviceStore = useDeviceStore.getState()
      
      // Получаем старый site_id перед обновлением
      const oldSiteId = deviceStore.siteId
      
      // Если site_id изменился (перерегистрация), очищаем старые данные
      if (oldSiteId && oldSiteId !== siteId) {
        console.log('[DeviceRegistration] Site ID changed, cleaning old data...')
        await LogsRepo.save('info', 'Site ID changed, cleaning old data', {
          old_site_id: oldSiteId,
          new_site_id: siteId,
        })
        
        // Удаляем все несинхронизированные события старого site_id
        const deletedEvents = await EventsRepo.deleteBySiteId(oldSiteId)
        console.log(`[DeviceRegistration] Deleted ${deletedEvents} old events`)
        
        // Очищаем кеш сотрудников (они могут быть из другого объекта)
        const deletedEmployees = await EmployeesRepo.clearAll()
        console.log(`[DeviceRegistration] Cleared ${deletedEmployees} cached employees`)
      } else if (!oldSiteId) {
        // Первая регистрация - очищаем все несинхронизированные события на всякий случай
        const deletedEvents = await EventsRepo.deleteUnsynced()
        console.log(`[DeviceRegistration] First registration, deleted ${deletedEvents} unsynced events`)
      }
      
      // Сохраняем credentials
      deviceStore.setDevice(deviceId, deviceSecret)
      deviceStore.setSiteId(siteId)

      // Выполняем миграции БД
      await initConfig()

      await LogsRepo.save('info', 'Device registration completed', {
        device_id: deviceId,
        site_id: siteId,
      })

      // Даем время завершиться всем транзакциям IndexedDB и Service Worker перед перезагрузкой
      // Это предотвращает ошибку "database connection is closing" в Service Worker
      console.log('[DeviceRegistration] Waiting for IndexedDB transactions and Service Worker to complete...')
      
      // Ждем завершения всех операций с БД (2 секунды для гарантии)
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Перезагружаем страницу для применения изменений
      console.log('[DeviceRegistration] Reloading page...')
      window.location.reload()
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to complete registration'
      await LogsRepo.save('error', `Registration completion failed: ${errorMsg}`, {
        device_id: deviceId,
        error: errorMsg,
      })
      throw error
    }
  }
}

